import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  Loader2,
  LockKeyhole,
  RefreshCw,
  Save,
  Shield,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { getRequestAuthToken } from '../../auth/appliedAuthToken';
import { useAuth } from '../../contexts/AuthContext';

type CommissioningStatus = 'READY_FOR_FIELD' | 'INSTALLED' | 'TESTED' | 'COMMISSIONED';
type ActuationStatus = 'LOCKED' | 'AUTHORIZED';
type Tab = 'valves' | 'pumps' | 'equipment';
type Filter = 'all' | 'install' | 'test' | 'commission' | 'authorized' | 'blocked';
type KpiKey = 'total' | 'ready' | 'installed' | 'tested' | 'commissioned' | 'authorized' | 'locked';
type IdentityFieldKey = 'nodeId' | 'model' | 'serial' | 'devEui' | 'ttnDeviceId' | 'firmware' | 'lastSeen';
type ChecklistKey = 'physical_installation_confirmed' | 'wiring_confirmed' | 'local_test_ok' | 'remote_test_ok';
type Feedback = 'success' | 'error' | 'conflict';
type LoadState = 'idle' | 'loading' | 'ready' | 'error' | 'unsupported';

interface OrganizationSummary {
  org_id: string;
  name: string;
  status: string | null;
}

interface FarmSummary {
  farm_id: string;
  name: string;
  status: string | null;
}

interface FarmDetailPayload {
  commissioning_supported: boolean;
}

interface CommonBinding {
  commissioning_status: CommissioningStatus;
  actuation_status: ActuationStatus;
  physical_installation_confirmed: boolean;
  wiring_confirmed: boolean;
  local_test_ok: boolean;
  remote_test_ok: boolean;
  command_authorized: boolean;
  lock_reason: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

interface ParcelBinding extends CommonBinding {
  binding_id: string;
  site: string;
  sector_id: string;
  sector_code: string;
  parcel: string;
  controller_node_id: string;
  channel_id: string;
  binding_status: string;
}

interface PumpBinding extends CommonBinding {
  site: string;
  function: string;
  pump_id: string;
  relay_node_id: string;
  relay_channel: string | null;
  relay_channel_status: string;
  interface_type: string | null;
  interface_type_status: string;
  direct_motor_switching: boolean;
  binding_status: string;
  state_age_policy_available: boolean;
}

interface NodeSummary {
  id: string;
  name: string | null;
  model: string | null;
  serial: string | null;
  dev_eui: string | null;
  ttn_device_id: string | null;
  firmware: string | null;
  last_seen_at: string | null;
  channels: string[];
}

interface BindingsPayload {
  schema: string | null;
  revision: string | null;
  farm_id: string;
  parcel_valve_bindings: ParcelBinding[];
  pumps: PumpBinding[];
}

interface Draft {
  commissioning_status: CommissioningStatus;
  actuation_status: ActuationStatus;
  physical_installation_confirmed: boolean;
  wiring_confirmed: boolean;
  local_test_ok: boolean;
  remote_test_ok: boolean;
  relay_channel?: string | null;
  interface_type?: string | null;
  expected_updated_at: string | null;
}

type Selection =
  | { kind: 'parcel'; value: ParcelBinding }
  | { kind: 'pump'; value: PumpBinding };

const COMMISSIONING_STATUSES: CommissioningStatus[] = [
  'READY_FOR_FIELD',
  'INSTALLED',
  'TESTED',
  'COMMISSIONED',
];
const TABS: Tab[] = ['valves', 'pumps', 'equipment'];
const FILTERS: Filter[] = ['all', 'install', 'test', 'commission', 'authorized', 'blocked'];
const RELAY_CHANNELS = ['K1', 'K2', 'K3', 'K4'];
const INTERFACE_TYPES = ['contactor_coil', 'vfd_run_input'];

const apiBase = (): string => process.env.REACT_APP_BACKEND_URL || '';

const filterButtonClass = (active: boolean): string => {
  if (active) {
    return 'min-h-[40px] shrink-0 rounded-full border border-emerald-700 bg-emerald-50 px-3 text-xs font-semibold text-emerald-800';
  }
  return 'min-h-[40px] shrink-0 rounded-full border border-gray-200 px-3 text-xs font-semibold text-gray-600';
};

const tabButtonClass = (active: boolean): string => {
  if (active) {
    return 'min-h-[44px] shrink-0 rounded-xl bg-emerald-700 px-4 text-sm font-semibold text-white';
  }
  return 'min-h-[44px] shrink-0 rounded-xl px-4 text-sm font-semibold text-gray-600 hover:bg-gray-50';
};

const channelClass = (assigned: boolean): string => {
  if (assigned) {
    return 'rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs text-emerald-700';
  }
  return 'rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs text-amber-800';
};

const feedbackClass = (feedback: Feedback): string => {
  if (feedback === 'success') {
    return 'rounded-xl bg-emerald-50 p-3 text-sm text-emerald-800';
  }
  return 'rounded-xl bg-red-50 p-3 text-sm text-red-800';
};

const hasAllProofs = (binding: CommonBinding): boolean =>
  binding.physical_installation_confirmed
  && binding.wiring_confirmed
  && binding.local_test_ok
  && binding.remote_test_ok;

export const eligibleForAuthorization = (selection: Selection): boolean => {
  const binding = selection.value;
  if (binding.commissioning_status !== 'COMMISSIONED' || !hasAllProofs(binding)) return false;
  if (selection.kind === 'pump') {
    return Boolean(
      selection.value.relay_channel
      && selection.value.interface_type
      && !selection.value.direct_motor_switching
      && selection.value.state_age_policy_available
      && selection.value.lock_reason !== 'PUMP_STATE_AGE_POLICY_MISSING',
    );
  }
  return true;
};

export const matchesCommissioningFilter = (binding: CommonBinding, filter: Filter): boolean => {
  switch (filter) {
    case 'install': return binding.commissioning_status === 'READY_FOR_FIELD';
    case 'test': return binding.commissioning_status === 'INSTALLED';
    case 'commission': return binding.commissioning_status === 'TESTED';
    case 'authorized': return binding.actuation_status === 'AUTHORIZED';
    case 'blocked': return binding.actuation_status === 'LOCKED' && Boolean(binding.lock_reason);
    default: return true;
  }
};

const AdminCommissioningAws: React.FC = () => {
  const { t } = useTranslation(['admin_common', 'common']);
  const { user } = useAuth();
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [farms, setFarms] = useState<FarmSummary[]>([]);
  const [orgId, setOrgId] = useState('');
  const [farmId, setFarmId] = useState('');
  const [bindings, setBindings] = useState<BindingsPayload | null>(null);
  const [nodes, setNodes] = useState<NodeSummary[]>([]);
  const [directoryState, setDirectoryState] = useState<LoadState>('loading');
  const [farmDirectoryError, setFarmDirectoryError] = useState(false);
  const [dataState, setDataState] = useState<LoadState>('idle');
  const [directoryReloadEpoch, setDirectoryReloadEpoch] = useState(0);
  const [reloadEpoch, setReloadEpoch] = useState(0);
  const [tab, setTab] = useState<Tab>('valves');
  const [filter, setFilter] = useState<Filter>('all');
  const [selection, setSelection] = useState<Selection | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  const token = getRequestAuthToken();
  const authHeaders = (): Record<string, string> => ({
    Authorization: `Bearer ${token || ''}`,
    'Content-Type': 'application/json',
  });

  useEffect(() => {
    if (user?.role !== 'superadmin' || !token) {
      setDirectoryState('error');
      return;
    }
    let mounted = true;
    setDirectoryState('loading');
    fetch(`${apiBase()}/api/admin/commissioning/organizations`, { headers: authHeaders() })
      .then(async (response) => {
        if (!response.ok) throw new Error('organizations_unavailable');
        return response.json() as Promise<OrganizationSummary[]>;
      })
      .then((rows) => {
        if (!mounted) return;
        if (!Array.isArray(rows)) throw new Error('organizations_invalid');
        setOrganizations(rows);
        setDirectoryState('ready');
      })
      .catch(() => {
        if (!mounted) return;
        setOrganizations([]);
        setDirectoryState('error');
      });
    return () => { mounted = false; };
  }, [user?.role, token, directoryReloadEpoch]);

  useEffect(() => {
    if (!orgId || user?.role !== 'superadmin' || !token) {
      setFarms([]);
      setFarmId('');
      setFarmDirectoryError(false);
      return;
    }
    let mounted = true;
    setFarmDirectoryError(false);
    fetch(`${apiBase()}/api/admin/commissioning/organizations/${encodeURIComponent(orgId)}/farms`, { headers: authHeaders() })
      .then(async (response) => {
        if (!response.ok) throw new Error('farms_unavailable');
        return response.json() as Promise<FarmSummary[]>;
      })
      .then((rows) => {
        if (!mounted) return;
        if (!Array.isArray(rows)) throw new Error('farms_invalid');
        setFarms(rows);
        setFarmId((current) => (
          current && rows.some((farm) => farm.farm_id === current) ? current : ''
        ));
        setFarmDirectoryError(false);
      })
      .catch(() => {
        if (!mounted) return;
        setFarms([]);
        setFarmId('');
        setFarmDirectoryError(true);
      });
    return () => { mounted = false; };
  }, [orgId, user?.role, token, directoryReloadEpoch]);

  useEffect(() => {
    if (!orgId || !farmId || user?.role !== 'superadmin' || !token) {
      setBindings(null);
      setNodes([]);
      setDataState('idle');
      return;
    }
    let mounted = true;
    setDataState('loading');
    const target = `/api/admin/commissioning/organizations/${encodeURIComponent(orgId)}/farms/${encodeURIComponent(farmId)}`;

    void (async () => {
      try {
        const detailResponse = await fetch(`${apiBase()}${target}`, { headers: authHeaders() });
        if (!detailResponse.ok) throw new Error('commissioning_unavailable');
        const detailPayload = await detailResponse.json() as FarmDetailPayload;
        if (!mounted) return;

        setSelection(null);
        setDraft(null);
        setFeedback(null);
        if (!detailPayload.commissioning_supported) {
          setBindings(null);
          setNodes([]);
          setDataState('unsupported');
          return;
        }

        const [bindingResponse, nodeResponse] = await Promise.all([
          fetch(`${apiBase()}${target}/bindings`, { headers: authHeaders() }),
          fetch(`${apiBase()}${target}/nodes`, { headers: authHeaders() }),
        ]);
        if (!bindingResponse.ok || !nodeResponse.ok) {
          throw new Error('commissioning_unavailable');
        }
        const bindingPayload = await bindingResponse.json() as BindingsPayload;
        const nodePayload = await nodeResponse.json() as NodeSummary[];
        if (!Array.isArray(bindingPayload.parcel_valve_bindings)
          || !Array.isArray(bindingPayload.pumps)
          || !Array.isArray(nodePayload)) {
          throw new Error('commissioning_invalid');
        }
        if (!mounted) return;
        if (bindingPayload.schema === null) {
          setBindings(null);
          setNodes([]);
          setDataState('unsupported');
          return;
        }
        setBindings(bindingPayload);
        setNodes(nodePayload);
        setDataState('ready');
      } catch {
        if (!mounted) return;
        setBindings(null);
        setNodes([]);
        setDataState('error');
      }
    })();

    return () => { mounted = false; };
  }, [orgId, farmId, user?.role, token, reloadEpoch]);

  const allBindings = useMemo<CommonBinding[]>(() => [
    ...(bindings?.parcel_valve_bindings || []),
    ...(bindings?.pumps || []),
  ], [bindings]);

  const kpis = useMemo(() => ({
    total: allBindings.length,
    ready: allBindings.filter((item) => item.commissioning_status === 'READY_FOR_FIELD').length,
    installed: allBindings.filter((item) => item.commissioning_status === 'INSTALLED').length,
    tested: allBindings.filter((item) => item.commissioning_status === 'TESTED').length,
    commissioned: allBindings.filter((item) => item.commissioning_status === 'COMMISSIONED').length,
    authorized: allBindings.filter((item) => item.actuation_status === 'AUTHORIZED').length,
    locked: allBindings.filter((item) => item.actuation_status === 'LOCKED').length,
  }), [allBindings]);

  const filteredParcels = useMemo(
    () => (bindings?.parcel_valve_bindings || []).filter((item) => matchesCommissioningFilter(item, filter)),
    [bindings, filter],
  );
  const filteredPumps = useMemo(
    () => (bindings?.pumps || []).filter((item) => matchesCommissioningFilter(item, filter)),
    [bindings, filter],
  );

  const nodeById = useMemo(() => new Map(nodes.map((node) => [node.id, node])), [nodes]);
  const assignedChannels = useMemo(() => {
    const result = new Map<string, Set<string>>();
    for (const binding of bindings?.parcel_valve_bindings || []) {
      const set = result.get(binding.controller_node_id) || new Set<string>();
      set.add(binding.channel_id);
      result.set(binding.controller_node_id, set);
    }
    for (const pump of bindings?.pumps || []) {
      if (!pump.relay_channel) continue;
      const set = result.get(pump.relay_node_id) || new Set<string>();
      set.add(pump.relay_channel);
      result.set(pump.relay_node_id, set);
    }
    return result;
  }, [bindings]);

  const authorizationSelection = useMemo<Selection | null>(() => {
    if (!selection || !draft) return null;
    if (selection.kind === 'parcel') {
      return { kind: 'parcel', value: { ...selection.value, ...draft } };
    }
    return { kind: 'pump', value: { ...selection.value, ...draft } };
  }, [selection, draft]);

  const statusLabel = (status: CommissioningStatus): string => {
    switch (status) {
      case 'READY_FOR_FIELD': return t('admin_common:commissioning.status.readyForField');
      case 'INSTALLED': return t('admin_common:commissioning.status.installed');
      case 'TESTED': return t('admin_common:commissioning.status.tested');
      case 'COMMISSIONED': return t('admin_common:commissioning.status.commissioned');
    }
  };

  const actuationLabel = (status: ActuationStatus): string => {
    if (status === 'AUTHORIZED') return t('admin_common:commissioning.actuation.authorized');
    return t('admin_common:commissioning.actuation.locked');
  };

  const kpiLabel = (key: KpiKey): string => {
    switch (key) {
      case 'total': return t('admin_common:commissioning.kpi.total');
      case 'ready': return t('admin_common:commissioning.kpi.ready');
      case 'installed': return t('admin_common:commissioning.kpi.installed');
      case 'tested': return t('admin_common:commissioning.kpi.tested');
      case 'commissioned': return t('admin_common:commissioning.kpi.commissioned');
      case 'authorized': return t('admin_common:commissioning.kpi.authorized');
      case 'locked': return t('admin_common:commissioning.kpi.locked');
    }
  };

  const tabLabel = (value: Tab): string => {
    switch (value) {
      case 'valves': return t('admin_common:commissioning.tabs.valves');
      case 'pumps': return t('admin_common:commissioning.tabs.pumps');
      case 'equipment': return t('admin_common:commissioning.tabs.equipment');
    }
  };

  const filterLabel = (value: Filter): string => {
    switch (value) {
      case 'all': return t('admin_common:commissioning.filters.all');
      case 'install': return t('admin_common:commissioning.filters.install');
      case 'test': return t('admin_common:commissioning.filters.test');
      case 'commission': return t('admin_common:commissioning.filters.commission');
      case 'authorized': return t('admin_common:commissioning.filters.authorized');
      case 'blocked': return t('admin_common:commissioning.filters.blocked');
    }
  };

  const identityFieldLabel = (key: IdentityFieldKey): string => {
    switch (key) {
      case 'nodeId': return t('admin_common:commissioning.fields.nodeId');
      case 'model': return t('admin_common:commissioning.fields.model');
      case 'serial': return t('admin_common:commissioning.fields.serial');
      case 'devEui': return t('admin_common:commissioning.fields.devEui');
      case 'ttnDeviceId': return t('admin_common:commissioning.fields.ttnDeviceId');
      case 'firmware': return t('admin_common:commissioning.fields.firmware');
      case 'lastSeen': return t('admin_common:commissioning.fields.lastSeen');
    }
  };

  const checklistLabel = (key: ChecklistKey): string => {
    switch (key) {
      case 'physical_installation_confirmed': return t('admin_common:commissioning.fields.physicalInstallation');
      case 'wiring_confirmed': return t('admin_common:commissioning.fields.wiring');
      case 'local_test_ok': return t('admin_common:commissioning.fields.localTest');
      case 'remote_test_ok': return t('admin_common:commissioning.fields.remoteTest');
    }
  };

  const feedbackLabel = (value: Feedback): string => {
    switch (value) {
      case 'success': return t('admin_common:commissioning.feedback.success');
      case 'error': return t('admin_common:commissioning.feedback.error');
      case 'conflict': return t('admin_common:commissioning.feedback.conflict');
    }
  };

  const openSelection = (next: Selection) => {
    const value = next.value;
    setSelection(next);
    setDraft({
      commissioning_status: value.commissioning_status,
      actuation_status: value.actuation_status,
      physical_installation_confirmed: value.physical_installation_confirmed,
      wiring_confirmed: value.wiring_confirmed,
      local_test_ok: value.local_test_ok,
      remote_test_ok: value.remote_test_ok,
      ...(next.kind === 'pump' ? {
        relay_channel: next.value.relay_channel,
        interface_type: next.value.interface_type,
      } : {}),
      expected_updated_at: value.updated_at,
    });
    setFeedback(null);
  };

  const closeDrawer = () => {
    if (saving) return;
    setSelection(null);
    setDraft(null);
    setFeedback(null);
  };

  const updateDraft = <K extends keyof Draft>(key: K, value: Draft[K]) => {
    setDraft((current) => current ? { ...current, [key]: value } : current);
  };

  const save = async () => {
    if (!selection || !draft || !orgId || !farmId || !token) return;
    if (draft.actuation_status === 'AUTHORIZED' && selection.value.actuation_status !== 'AUTHORIZED') {
      const confirmed = window.confirm(t('admin_common:commissioning.confirmAuthorized'));
      if (!confirmed) return;
    }
    setSaving(true);
    setFeedback(null);
    const base = `/api/admin/commissioning/organizations/${encodeURIComponent(orgId)}/farms/${encodeURIComponent(farmId)}`;
    const path = selection.kind === 'parcel'
      ? `${base}/parcel-valve-bindings/${encodeURIComponent(selection.value.binding_id)}`
      : `${base}/pumps/${encodeURIComponent(selection.value.pump_id)}`;
    try {
      const response = await fetch(`${apiBase()}${path}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify(draft),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null) as { detail?: string } | null;
        if (response.status === 409 && payload?.detail === 'COMMISSIONING_UPDATE_CONFLICT') {
          setFeedback('conflict');
        } else {
          setFeedback('error');
        }
        return;
      }
      setFeedback('success');
      setReloadEpoch((value) => value + 1);
    } catch {
      setFeedback('error');
    } finally {
      setSaving(false);
    }
  };

  if (user?.role !== 'superadmin') {
    return (
      <div className="flex min-h-[60vh] items-center justify-center" data-testid="admin-commissioning-denied">
        <div className="text-center">
          <Shield className="mx-auto mb-4 h-14 w-14 text-red-500" />
          <h1 className="text-2xl font-bold text-gray-900">{t('admin_common:accessDenied')}</h1>
          <p className="mt-2 text-gray-600">{t('admin_common:superadminRequired')}</p>
        </div>
      </div>
    );
  }

  const kpiCards: ReadonlyArray<readonly [KpiKey, number]> = [
    ['total', kpis.total], ['ready', kpis.ready], ['installed', kpis.installed],
    ['tested', kpis.tested], ['commissioned', kpis.commissioned],
    ['authorized', kpis.authorized], ['locked', kpis.locked],
  ];

  return (
    <div className="mx-auto max-w-7xl space-y-5 p-4 sm:p-6" data-testid="admin-commissioning-r1">
      <section className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-emerald-700">
            <ClipboardCheck className="h-5 w-5" />
            <span className="text-sm font-semibold">{t('admin_common:commissioning.eyebrow')}</span>
          </div>
          <h1 className="mt-1 text-2xl font-bold text-gray-900">{t('admin_common:commissioning.title')}</h1>
          <p className="mt-1 text-sm text-gray-600">{t('admin_common:commissioning.subtitle')}</p>
        </div>
        <button
          type="button"
          onClick={() => {
            setDirectoryReloadEpoch((value) => value + 1);
            setReloadEpoch((value) => value + 1);
          }}
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700"
        >
          <RefreshCw className="h-4 w-4" />{t('common:refresh')}
        </button>
      </section>

      <section className="grid gap-3 rounded-2xl border border-gray-200 bg-white p-4 sm:grid-cols-2">
        <label className="space-y-1 text-sm font-medium text-gray-700">
          <span>{t('admin_common:commissioning.organization')}</span>
          <select
            value={orgId}
            onChange={(event) => {
              setOrgId(event.target.value);
              setFarmId('');
              setFarms([]);
              setFarmDirectoryError(false);
            }}
            disabled={directoryState !== 'ready'}
            className="min-h-[44px] w-full rounded-xl border border-gray-200 bg-white px-3"
            data-testid="commissioning-org-select"
          >
            <option value="">{t('admin_common:commissioning.chooseOrganization')}</option>
            {organizations.map((org) => <option key={org.org_id} value={org.org_id}>{org.name}</option>)}
          </select>
        </label>
        <label className="space-y-1 text-sm font-medium text-gray-700">
          <span>{t('admin_common:commissioning.farm')}</span>
          <select
            value={farmId}
            onChange={(event) => setFarmId(event.target.value)}
            disabled={!orgId}
            className="min-h-[44px] w-full rounded-xl border border-gray-200 bg-white px-3"
            data-testid="commissioning-farm-select"
          >
            <option value="">{t('admin_common:commissioning.chooseFarm')}</option>
            {farms.map((farm) => <option key={farm.farm_id} value={farm.farm_id}>{farm.name}</option>)}
          </select>
        </label>
      </section>

      {(directoryState === 'error' || farmDirectoryError || dataState === 'error') && (
        <section className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800" role="status" data-testid="commissioning-error">
          <div className="flex items-start gap-2"><AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />{t('admin_common:commissioning.unavailable')}</div>
        </section>
      )}

      {dataState === 'unsupported' && (
        <section className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" role="status" data-testid="commissioning-unsupported">
          <div className="flex items-start gap-2"><AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />{t('admin_common:commissioning.unsupported')}</div>
        </section>
      )}

      {dataState === 'loading' && (
        <section className="flex min-h-[180px] items-center justify-center rounded-2xl border border-gray-200 bg-white" data-testid="commissioning-loading">
          <Loader2 className="h-7 w-7 animate-spin text-gray-400" />
        </section>
      )}

      {dataState === 'ready' && bindings && (
        <>
          <section className="grid grid-cols-2 gap-3 sm:grid-cols-4 xl:grid-cols-7">
            {kpiCards.map(([key, value]) => (
              <div key={key} className="rounded-xl border border-gray-200 bg-white p-3">
                <div className="text-xs font-medium text-gray-500">{kpiLabel(key)}</div>
                <div className="mt-1 text-2xl font-bold text-gray-900">{value}</div>
              </div>
            ))}
          </section>

          <section className="rounded-2xl border border-gray-200 bg-white">
            <div className="flex gap-1 overflow-x-auto border-b border-gray-200 p-2">
              {TABS.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setTab(item)}
                  className={tabButtonClass(tab === item)}
                  data-testid={`commissioning-tab-${item}`}
                >
                  {tabLabel(item)}
                </button>
              ))}
            </div>

            {tab !== 'equipment' && (
              <div className="flex gap-2 overflow-x-auto border-b border-gray-100 p-3" data-testid="commissioning-filters">
                {FILTERS.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setFilter(item)}
                    className={filterButtonClass(filter === item)}
                  >
                    {filterLabel(item)}
                  </button>
                ))}
              </div>
            )}

            {tab === 'valves' && (
              <div className="divide-y divide-gray-100" data-testid="commissioning-valve-list">
                {filteredParcels.length === 0 ? (
                  <div className="p-8 text-center text-sm text-gray-500">{t('admin_common:commissioning.empty')}</div>
                ) : filteredParcels.map((binding) => {
                  const node = nodeById.get(binding.controller_node_id);
                  return (
                    <button
                      key={binding.binding_id}
                      type="button"
                      onClick={() => openSelection({ kind: 'parcel', value: binding })}
                      className="grid min-h-[72px] w-full gap-2 p-4 text-left hover:bg-gray-50 sm:grid-cols-[1.2fr_1fr_1fr_auto] sm:items-center"
                      data-testid={`commissioning-binding-${binding.binding_id}`}
                    >
                      <div><div className="font-semibold text-gray-900">{binding.parcel}</div><div className="text-xs text-gray-500">{binding.site} · {binding.sector_code}</div></div>
                      <div className="text-sm text-gray-700">{node?.name || binding.controller_node_id}<div className="text-xs text-gray-500">{binding.channel_id}</div></div>
                      <div><div className="text-sm font-semibold text-gray-800">{statusLabel(binding.commissioning_status)}</div><div className="text-xs text-red-600">{binding.lock_reason || actuationLabel(binding.actuation_status)}</div></div>
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    </button>
                  );
                })}
              </div>
            )}

            {tab === 'pumps' && (
              <div className="divide-y divide-gray-100" data-testid="commissioning-pump-list">
                {filteredPumps.length === 0 ? (
                  <div className="p-8 text-center text-sm text-gray-500">{t('admin_common:commissioning.empty')}</div>
                ) : filteredPumps.map((pump) => {
                  const node = nodeById.get(pump.relay_node_id);
                  return (
                    <button
                      key={pump.pump_id}
                      type="button"
                      onClick={() => openSelection({ kind: 'pump', value: pump })}
                      className="grid min-h-[72px] w-full gap-2 p-4 text-left hover:bg-gray-50 sm:grid-cols-[1.2fr_1fr_1fr_auto] sm:items-center"
                      data-testid={`commissioning-pump-${pump.pump_id}`}
                    >
                      <div><div className="font-semibold text-gray-900">{pump.site}</div><div className="text-xs text-gray-500">{pump.function}</div></div>
                      <div className="text-sm text-gray-700">{node?.name || pump.relay_node_id}<div className="text-xs text-gray-500">{pump.relay_channel || '—'}</div></div>
                      <div><div className="text-sm font-semibold text-gray-800">{statusLabel(pump.commissioning_status)}</div><div className="text-xs text-red-600">{pump.lock_reason || actuationLabel(pump.actuation_status)}</div></div>
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    </button>
                  );
                })}
              </div>
            )}

            {tab === 'equipment' && (
              <div className="divide-y divide-gray-100" data-testid="commissioning-equipment-list">
                {nodes.length === 0 ? (
                  <div className="p-8 text-center text-sm text-gray-500">{t('admin_common:commissioning.empty')}</div>
                ) : nodes.map((node) => (
                  <article key={node.id} className="p-4">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                      <div><div className="font-semibold text-gray-900">{node.name || node.id}</div><div className="text-xs text-gray-500">{node.model || '—'} · {node.id}</div></div>
                      <div className="text-xs text-gray-500">{t('admin_common:commissioning.fields.lastSeen')}: {node.last_seen_at || '—'}</div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {node.channels.map((channel) => {
                        const assigned = assignedChannels.get(node.id)?.has(channel) || false;
                        return (
                          <span key={channel} className={channelClass(assigned)}>
                            {channel} · {assigned ? t('admin_common:commissioning.assigned') : t('admin_common:commissioning.unassigned')}
                          </span>
                        );
                      })}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        </>
      )}

      {selection && draft && (
        <div className="fixed inset-0 z-[80] flex justify-end bg-black/40" data-testid="commissioning-drawer">
          <div className="h-full w-full max-w-xl overflow-y-auto bg-white shadow-2xl">
            <div className="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white p-4">
              <div><div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">{t('admin_common:commissioning.drawerTitle')}</div><div className="font-semibold text-gray-900">{selection.kind === 'parcel' ? selection.value.parcel : selection.value.site}</div></div>
              <button type="button" onClick={closeDrawer} className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-xl hover:bg-gray-100" aria-label={t('admin_common:commissioning.close')}><X className="h-5 w-5" /></button>
            </div>
            <div className="space-y-5 p-4 sm:p-6">
              {(() => {
                const nodeId = selection.kind === 'parcel' ? selection.value.controller_node_id : selection.value.relay_node_id;
                const node = nodeById.get(nodeId);
                const identityFields: ReadonlyArray<readonly [IdentityFieldKey, string | null | undefined]> = [
                  ['nodeId', nodeId], ['model', node?.model], ['serial', node?.serial], ['devEui', node?.dev_eui],
                  ['ttnDeviceId', node?.ttn_device_id], ['firmware', node?.firmware], ['lastSeen', node?.last_seen_at],
                ];
                return (
                  <section className="rounded-2xl border border-gray-200 p-4">
                    <h2 className="font-semibold text-gray-900">{t('admin_common:commissioning.identity')}</h2>
                    <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                      {identityFields.map(([key, value]) => <div key={key}><dt className="text-xs text-gray-500">{identityFieldLabel(key)}</dt><dd className="mt-0.5 break-all text-gray-800">{value || '—'}</dd></div>)}
                    </dl>
                  </section>
                );
              })()}

              <section className="rounded-2xl border border-gray-200 p-4">
                <h2 className="font-semibold text-gray-900">{t('admin_common:commissioning.assignment')}</h2>
                <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
                  <div><dt className="text-xs text-gray-500">{t('admin_common:commissioning.fields.site')}</dt><dd>{selection.value.site}</dd></div>
                  {selection.kind === 'parcel' && <><div><dt className="text-xs text-gray-500">{t('admin_common:commissioning.fields.sector')}</dt><dd>{selection.value.sector_code}</dd></div><div><dt className="text-xs text-gray-500">{t('admin_common:commissioning.fields.parcel')}</dt><dd>{selection.value.parcel}</dd></div><div><dt className="text-xs text-gray-500">{t('admin_common:commissioning.fields.channel')}</dt><dd>{selection.value.channel_id}</dd></div></>}
                  {selection.kind === 'pump' && <div><dt className="text-xs text-gray-500">{t('admin_common:commissioning.fields.nodeId')}</dt><dd>{selection.value.relay_node_id}</dd></div>}
                </dl>
              </section>

              <section className="rounded-2xl border border-gray-200 p-4">
                <h2 className="font-semibold text-gray-900">{t('admin_common:commissioning.commissioningBlock')}</h2>
                <div className="mt-3 space-y-3">
                  {([
                    'physical_installation_confirmed',
                    'wiring_confirmed',
                    'local_test_ok',
                    'remote_test_ok',
                  ] as ChecklistKey[]).map((key) => (
                    <label key={key} className="flex min-h-[44px] items-center gap-3 rounded-xl border border-gray-200 px-3">
                      <input type="checkbox" checked={draft[key]} onChange={(event) => updateDraft(key, event.target.checked)} className="h-5 w-5" />
                      <span className="text-sm font-medium text-gray-700">{checklistLabel(key)}</span>
                    </label>
                  ))}
                  <label className="block text-sm font-medium text-gray-700">
                    <span>{t('admin_common:commissioning.fields.commissioningStatus')}</span>
                    <select value={draft.commissioning_status} onChange={(event) => updateDraft('commissioning_status', event.target.value as CommissioningStatus)} className="mt-1 min-h-[44px] w-full rounded-xl border border-gray-200 px-3">
                      {COMMISSIONING_STATUSES.map((item) => <option key={item} value={item}>{statusLabel(item)}</option>)}
                    </select>
                  </label>
                  {selection.kind === 'pump' && (
                    <div className="grid gap-3 sm:grid-cols-2">
                      <label className="text-sm font-medium text-gray-700"><span>{t('admin_common:commissioning.fields.relayChannel')}</span><select value={draft.relay_channel || ''} onChange={(event) => updateDraft('relay_channel', event.target.value || null)} className="mt-1 min-h-[44px] w-full rounded-xl border border-gray-200 px-3"><option value="">—</option>{RELAY_CHANNELS.map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
                      <label className="text-sm font-medium text-gray-700"><span>{t('admin_common:commissioning.fields.interfaceType')}</span><select value={draft.interface_type || ''} onChange={(event) => updateDraft('interface_type', event.target.value || null)} className="mt-1 min-h-[44px] w-full rounded-xl border border-gray-200 px-3"><option value="">—</option>{INTERFACE_TYPES.map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
                    </div>
                  )}
                  <label className="block text-sm font-medium text-gray-700">
                    <span>{t('admin_common:commissioning.fields.actuationStatus')}</span>
                    <select
                      value={draft.actuation_status}
                      onChange={(event) => updateDraft('actuation_status', event.target.value as ActuationStatus)}
                      className="mt-1 min-h-[44px] w-full rounded-xl border border-gray-200 px-3 disabled:bg-gray-100"
                    >
                      <option value="LOCKED">{actuationLabel('LOCKED')}</option>
                      <option value="AUTHORIZED" disabled={!authorizationSelection || !eligibleForAuthorization(authorizationSelection)}>{actuationLabel('AUTHORIZED')}</option>
                    </select>
                  </label>
                </div>
              </section>

              <section className="rounded-2xl border border-gray-200 p-4 text-sm">
                <div className="flex items-start gap-2">
                  {selection.value.lock_reason ? <LockKeyhole className="mt-0.5 h-4 w-4 shrink-0 text-red-600" /> : <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />}
                  <div><div className="font-semibold text-gray-800">{t('admin_common:commissioning.fields.lockReason')}</div><div className="mt-1 break-words text-gray-600">{selection.value.lock_reason || '—'}</div></div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-gray-500"><div>{t('admin_common:commissioning.fields.lastModified')}: {selection.value.updated_at || '—'}</div><div>{t('admin_common:commissioning.fields.modifiedBy')}: {selection.value.updated_by || '—'}</div></div>
              </section>

              {feedback && <div className={feedbackClass(feedback)} role="status" data-testid="commissioning-save-feedback">{feedbackLabel(feedback)}</div>}

              <button
                type="button"
                onClick={() => { void save(); }}
                disabled={saving}
                className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl bg-emerald-700 px-4 text-sm font-bold text-white disabled:opacity-50"
                data-testid="commissioning-save"
              >
                {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
                {saving ? t('admin_common:commissioning.saving') : t('admin_common:commissioning.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCommissioningAws;